/**
 * ゴミ箱（docs/01-requirements.md §5.13）。
 *
 * - 自動削除期限は設けない。明示的な完全削除まで残ることを画面上で明言する。
 * - 完全削除はリビジョン・画像まで消える取り返しのつかない操作なので確認を求める。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@app/api/client';
import { AppHeader } from '@app/components/AppHeader';
import { errorMessage, ErrorNotice } from '@app/components/ErrorNotice';
import { subjectLabel } from '@app/components/subject-label';
import { useToast } from '@app/ui/toast';
import { deriveSummaryText, displayTitle } from '@shared/markdown-input';
import type { NoteSummary } from '@shared/types';

const PAGE_SIZE = 30;
/** 完全削除の確認ダイアログに載せる本文の長さ。confirm は長文に向かないため短くする。 */
const PURGE_SUMMARY_LENGTH = 60;

export function TrashPage() {
  const toast = useToast();
  const [notes, setNotes] = useState<NoteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const cursorRef = useRef<string | null>(null);

  const load = useCallback(async (reset: boolean) => {
    setLoading(true);
    try {
      const result = await api.listNotes({
        trashed: true,
        limit: PAGE_SIZE,
        ...(reset || !cursorRef.current ? {} : { cursor: cursorRef.current }),
      });
      setNotes((prev) => (reset ? result.notes : [...prev, ...result.notes]));
      cursorRef.current = result.next_cursor;
      setDone(result.next_cursor === null);
      setError(null);
    } catch (err: unknown) {
      setError(err);
      setDone(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cursorRef.current = null;
    void load(true);
  }, [load]);

  const restore = useCallback(
    (note: NoteSummary) => {
      void (async () => {
        setBusyId(note.id);
        try {
          await api.restoreNote(note.id);
          setNotes((prev) => prev.filter((n) => n.id !== note.id));
          toast.show('ノートを復元しました');
        } catch (err: unknown) {
          toast.show(`復元できませんでした: ${errorMessage(err)}`);
        } finally {
          setBusyId(null);
        }
      })();
    },
    [toast],
  );

  const purge = useCallback(
    (note: NoteSummary) => {
      // タイトルは「無題」になり得る（ADR-031）。取り返しのつかない操作なので、
      // どれを消すのか分かるよう本文の冒頭を併記する（docs/06-open-questions.md #16）。
      const title = displayTitle(note.title);
      const summary = deriveSummaryText(note.excerpt);
      const bodyLine =
        summary === ''
          ? ''
          : `本文: ${summary.length > PURGE_SUMMARY_LENGTH ? `${summary.slice(0, PURGE_SUMMARY_LENGTH)}…` : summary}\n`;
      const ok = window.confirm(
        `「${title}」を完全に削除します。\n` +
          bodyLine +
          '本文・履歴（リビジョン）・添付画像がすべて消え、元に戻せません。\n' +
          '削除してよろしいですか？',
      );
      if (!ok) return;
      void (async () => {
        setBusyId(note.id);
        try {
          await api.purgeNote(note.id);
          setNotes((prev) => prev.filter((n) => n.id !== note.id));
          toast.show('完全に削除しました');
        } catch (err: unknown) {
          toast.show(`削除できませんでした: ${errorMessage(err)}`);
        } finally {
          setBusyId(null);
        }
      })();
    },
    [toast],
  );

  return (
    <div className="page">
      <AppHeader title="ゴミ箱" backTo="/" />

      <main className="page__body">
        <p className="al-notice al-notice--cream notice-block">
          ゴミ箱の中身は自動では消えません。完全に削除するまで、ここに残り続けます。
        </p>

        {error != null && <ErrorNotice error={error} onRetry={() => void load(true)} />}

        {!loading && notes.length === 0 && error == null && (
          <p className="muted centered-note">ゴミ箱は空です。</p>
        )}

        <ul className="timeline">
          {notes.map((note) => (
            <li key={note.id} className="note-card">
              <div className="note-card__meta">
                <span className="note-card__date">
                  {subjectLabel(note.subject_date, note.subject_date_end)}
                </span>
              </div>
              <div className="note-card__title">{displayTitle(note.title)}</div>
              <p className="note-card__excerpt muted">{note.excerpt}</p>
              <div className="note-card__actions">
                <button
                  type="button"
                  className="al-button al-button--secondary"
                  disabled={busyId === note.id}
                  onClick={() => restore(note)}
                >
                  復元
                </button>
                <button
                  type="button"
                  className="al-button al-button--secondary danger-button"
                  disabled={busyId === note.id}
                  onClick={() => purge(note)}
                >
                  完全に削除
                </button>
              </div>
            </li>
          ))}
        </ul>

        {loading && <p className="muted centered-note">読み込み中…</p>}
        {!loading && !done && (
          <p className="centered-note">
            <button
              type="button"
              className="al-button al-button--secondary"
              onClick={() => void load(false)}
            >
              さらに読み込む
            </button>
          </p>
        )}
      </main>
    </div>
  );
}
